import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuthCallback() {
  const navigate = useNavigate();
  const { updateUser, setAuthToken } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use useRef to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          console.error('No session_id found in URL');
          navigate('/login', { replace: true });
          return;
        }

        // Exchange session_id for user data
        const response = await fetch(`${API_URL}/api/auth/session?session_id=${sessionId}`, {
          method: 'GET',
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to exchange session');
        }

        const data = await response.json();
        
        // Store the token
        if (data.token) {
          setAuthToken(data.token);
        }
        
        updateUser(data.user);

        // Solo cuenta como conversión de registro si el backend confirma que la cuenta se
        // acaba de crear (is_new_user) — nunca inferir esto de datos del propio frontend
        // (ej. onboarding_completed) porque un usuario existente que nunca completó el
        // onboarding se contaría como alta nueva cada vez que vuelve a entrar por Google.
        if (data.is_new_user && typeof window.gtag === 'function') {
          window.gtag('event', 'sign_up', { method: 'google' });
          window.gtag('event', 'conversion', {
            send_to: 'AW-18303994422/_iJ2COH4quscELaUg5hE',
            value: 1,
            currency: 'MXN',
          });
        }

        // Clear the hash from URL
        window.history.replaceState(null, '', window.location.pathname);

        // Navigate based on onboarding status
        if (data.user.onboarding_completed) {
          navigate('/dashboard', { replace: true, state: { user: data.user } });
        } else {
          navigate('/onboarding', { replace: true, state: { user: data.user } });
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/login', { replace: true });
      }
    };

    processCallback();
  }, [navigate, updateUser, setAuthToken]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" />
        <p className="text-muted-foreground">Completando inicio de sesión...</p>
      </div>
    </div>
  );
}
